<script setup lang="ts">
import {
  checkoutReadingLine,
  type CheckoutProgressStep,
} from '~/composables/useCheckoutProgress'

/**
 * The checkout's step tracker, rendered by whichever shell owns the header.
 *
 * It answers three questions that the numbered cards, on their own, only answer
 * while they happen to be on screen: how much of this is left, which step am I
 * in, and what have I skipped. The last one is the reason it exists — the flow
 * is long enough that people scroll past a step, and the only thing that used
 * to tell them was the CTA refusing at the very bottom.
 *
 * State comes from `useCheckoutProgress`, published by `CheckoutExperience`.
 * This component computes NOTHING about completion; it only renders it and
 * scrolls. The one thing it owns is which step you are currently looking at.
 *
 * `<ClientOnly>` is not decoration. The shells render the header ABOVE the
 * checkout, so on the server the flow has not published anything by the time
 * this renders — but `useState` carries the published value into the client
 * payload, so a client render would produce a full rail where the server
 * produced an empty one. Same class of bug as a permission-gated nav item, and
 * the same remedy: render on the client only, behind a fallback of the same
 * height so nothing below it moves.
 */

const { t: $t } = useI18n()
const progress = useCheckoutProgress()
const { steps, activeKey, percent, completed, total, outstanding, attentionStep, suppressed } =
  progress

const listOpen = ref(false)

// ── Step appearance ───────────────────────────────────────────────────────
/**
 * The marker is the step card's own circle, in the same colours — an amber
 * warning, a green tick, a numbered grey ring — so a chip up here and a card
 * down there can be matched without anyone explaining that they correspond.
 * `current` is the one state the cards have no need of: a card cannot say
 * "you are reading me", and it is the whole point of a tracker that can.
 */
type StepTone = 'attention' | 'done' | 'asking' | 'idle'

/** Waiting on the customer: the state this whole component exists to point at. */
function isAsking(step: CheckoutProgressStep): boolean {
  return step.reachable && step.blocked
}

function toneOf(step: CheckoutProgressStep): StepTone {
  if (step.attention && step.reachable) return 'attention'
  if (step.done) return 'done'
  if (isAsking(step)) return 'asking'
  return 'idle'
}

const MARKER_TONE: Record<StepTone, string> = {
  attention: 'bg-warning text-inverted ring-0',
  done: 'bg-success text-inverted ring-0',
  // Outlined in the brand colour rather than filled: filled reads as finished,
  // and this is the opposite of finished.
  asking: 'bg-primary/10 text-primary ring-1 ring-inset ring-primary/40',
  // Nothing to do here. The quietest thing on the rail, deliberately.
  idle: 'bg-elevated text-dimmed ring-1 ring-inset ring-default',
}

/**
 * "You are here" has NO marker of its own — the chip's own tinted pill says it.
 *
 * A solid brand-coloured circle on the step you happen to be scrolled to was
 * the loudest thing on the rail, outshouting the step that actually wanted an
 * answer two chips along. Position is context; the outstanding choice is the
 * message.
 */

/**
 * Emphasis runs the opposite way to the marker colours, and deliberately.
 *
 * A finished step is a green tick and quiet text: it wants no more attention.
 * The step still owed is the loudest thing on the rail, because "you left
 * something behind" is the only sentence this component exists to say — and
 * the first version had it backwards, drawing the one outstanding step fainter
 * than the six already done.
 */
function chipTextClass(step: CheckoutProgressStep): string {
  if (step.attention && step.reachable) return 'text-warning font-semibold'
  if (isAsking(step)) return 'text-highlighted font-semibold'
  if (step.key === activeKey.value) return 'text-highlighted'
  if (step.done) return 'text-muted'
  return 'text-dimmed'
}

/**
 * What to say underneath a step's name.
 *
 * The VALUE wins wherever there is one, because most of this flow arrives
 * pre-answered and "ready" says nothing a customer can check. "Belo Horizonte"
 * and "Mensal" are what let them see, from the header, that the preselections
 * are the ones they want. The state only speaks where there is no value to
 * report — or where something is wrong, which outranks everything.
 */
function detailLabel(step: CheckoutProgressStep): string {
  if (step.attention && step.reachable) return stateLabel(step)
  return step.value || stateLabel(step)
}

/**
 * The step's state in words, for the screen reader that has just read the
 * label. It carries the STATE only: the visible label sits in the same button,
 * and repeating it announced every step twice.
 */
function stateLabel(step: CheckoutProgressStep): string {
  if (step.attention && step.reachable)
    return $t('billing.page.checkout.progress.state.attention')
  if (step.done) return $t('billing.page.checkout.progress.state.done')
  if (!step.reachable) return $t('billing.page.checkout.progress.state.locked')
  if (isAsking(step)) return $t('billing.page.checkout.progress.state.asking')
  if (step.requirement === 'optional')
    return $t('billing.page.checkout.steps.optional')
  return $t('billing.page.checkout.progress.state.defaulted')
}

/** The step the compact bar names. Falls back to the first thing outstanding,
 *  so the phone bar is never blank before the customer has scrolled at all. */
/**
 * What the compact bar names.
 *
 * The next thing OWED leads, not the step the page is scrolled to: the rail is
 * a list of what is left now, and on a phone it has room for exactly one line.
 * "Where am I" is still on the chip via `aria-current`; "what do I still have
 * to do" is the question this line answers.
 */
const headlineStep = computed(
  () => outstanding.value[0] ?? progress.activeStep.value ?? steps.value[0] ?? null,
)

// ── Scroll spy: which step is the customer actually looking at ────────────
/**
 * Read from the cards' own positions rather than from any click, because most
 * movement through this page is scrolling — the tracker has to follow a
 * customer who never touches it.
 *
 * The listener is on `window` in the CAPTURE phase: scroll events do not
 * bubble, and the console shell scrolls an inner panel rather than the
 * document, so a plain bubbling listener would never fire there.
 */
let spyFrame = 0

function recomputeActive() {
  if (spyFrame) return
  spyFrame = requestAnimationFrame(() => {
    spyFrame = 0
    const spied = steps.value.filter((s) => s.reachable && !s.final)
    let current: CheckoutProgressStep | null = null
    let lastEl: HTMLElement | null = null
    let line: number | null = null

    for (const step of spied) {
      const el = document.getElementById(step.anchor)
      if (!el) continue
      // Measured from the first card that exists, because that is what tells
      // us which of the two shells is doing the scrolling.
      if (line === null) line = checkoutReadingLine(el)
      lastEl = el
      if (el.getBoundingClientRect().top <= line) current = step
      else break
    }

    // Scrolled past the last card: the only thing left is to confirm, so the
    // tracker should say so rather than stay stuck on the step above it.
    const closing = steps.value.find((s) => s.final && s.reachable)
    if (closing && lastEl && line !== null && lastEl.getBoundingClientRect().bottom <= line) {
      current = closing
    }

    progress.setActive(current?.key ?? spied[0]?.key ?? null)
  })
}

// ── The overflow rail (desktop) ───────────────────────────────────────────
const rail = ref<HTMLElement | null>(null)
const atStart = ref(true)
const atEnd = ref(true)
const overflows = ref(false)

function syncEdges() {
  const el = rail.value
  if (!el) return
  overflows.value = el.scrollWidth > el.clientWidth + 1
  atStart.value = el.scrollLeft <= 1
  atEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
}

/**
 * The fade is a MASK, not the gradient overlay the instance rail uses.
 *
 * This bar is translucent over whatever the page is scrolling underneath it, so
 * a gradient painted in the surface colour would show as a pale smear across
 * it. Masking the content itself is background-agnostic and dark-mode-proof.
 */
const railMask = computed(() => {
  if (!overflows.value) return undefined
  const start = atStart.value ? 'black 0' : 'transparent 0, black 32px'
  const end = atEnd.value ? 'black 100%' : 'black calc(100% - 32px), transparent 100%'
  return `linear-gradient(to right, ${start}, ${end})`
})

function nudge(direction: -1 | 1) {
  rail.value?.scrollBy({
    left: direction * Math.max(200, (rail.value.clientWidth || 0) * 0.7),
    behavior: 'smooth',
  })
}

/** Keep "you are here" on screen without the customer hunting for it. */
function revealActive() {
  nextTick(() => {
    const el = rail.value?.querySelector<HTMLElement>('[data-active="true"]')
    // `scrollLeft` rather than `scrollIntoView`: the rail lives in a sticky
    // header, and `scrollIntoView` on a child is entitled to scroll the PAGE
    // too — which would fight the very scroll that moved the active step.
    if (el && rail.value) {
      rail.value.scrollTo({
        left: el.offsetLeft - rail.value.clientWidth / 2 + el.offsetWidth / 2,
        behavior: 'smooth',
      })
    }
    syncEdges()
  })
}

let railObserver: ResizeObserver | null = null

/**
 * The rail appears LATER than this component does — it is inside `ClientOnly`
 * and renders nothing until the flow has published its steps — so observing it
 * from `onMounted` observed nothing at all, permanently. Its width also changes
 * without the window resizing (the dashboard sidebar collapses), which is the
 * case the observer exists for.
 */
watch(
  rail,
  (el) => {
    railObserver?.disconnect()
    railObserver = null
    if (el && typeof ResizeObserver !== 'undefined') {
      railObserver = new ResizeObserver(syncEdges)
      railObserver.observe(el)
    }
    syncEdges()
  },
  { flush: 'post' },
)

onMounted(() => {
  recomputeActive()
  syncEdges()
  window.addEventListener('scroll', recomputeActive, { passive: true, capture: true })
  window.addEventListener('resize', recomputeActive, { passive: true })
  window.addEventListener('resize', syncEdges, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', recomputeActive, { capture: true })
  window.removeEventListener('resize', recomputeActive)
  window.removeEventListener('resize', syncEdges)
  railObserver?.disconnect()
  if (spyFrame) cancelAnimationFrame(spyFrame)
})

watch(activeKey, revealActive)
// The flow reveals steps as it goes — the rail gains chips mid-checkout.
watch(steps, () => {
  nextTick(syncEdges)
  recomputeActive()
})

// ── Actions ───────────────────────────────────────────────────────────────
function jumpTo(step: CheckoutProgressStep) {
  if (!step.reachable) return
  listOpen.value = false
  progress.goTo(step)
}

function jumpToOutstanding() {
  listOpen.value = false
  progress.goToNextOutstanding()
}

/** The donut on the phone bar. r=13 keeps the whole control at 32px. */
const RING_CIRCUMFERENCE = 2 * Math.PI * 13
const ringOffset = computed(
  () => RING_CIRCUMFERENCE * (1 - Math.min(100, Math.max(0, percent.value)) / 100),
)
</script>

<template>
  <!-- No surface of its own: each shell supplies the chrome, because they
       differ in a way the component cannot guess. The console draws it on an
       opaque panel header; the public page floats it over scrolling content
       and needs the blur. Baking one in meant the other had to override it
       with same-specificity utilities, which is a coin toss. -->
  <div
    data-checkout-progress
    :aria-label="$t('billing.page.checkout.progress.title')"
    role="group"
  >
    <ClientOnly>
      <template #fallback>
        <div class="h-[3.25rem]" aria-hidden="true" />
      </template>

      <!-- Nothing to guide anyone through yet, or something else owns the
           screen. `suppressed` is the commit sheet: it draws its own rail, and
           a bottom sheet leaves the top of the page showing above it, so
           without this a phone showed two step rails at once — the dimmed one
           describing the configurator being the first thing you read. The
           spacer stays so the sticky header keeps its height and
           `checkoutStickyOffset()` measures the same thing either way. -->
      <div
        v-if="steps.length === 0 || suppressed"
        class="h-[3.25rem]"
        aria-hidden="true"
      />

      <div v-else>
        <!-- ── Compact bar: phones and narrow tablets ──────────────── -->
        <div class="flex items-center gap-2 px-3 py-2 lg:hidden">
          <button
            type="button"
            class="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg py-0.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            :aria-expanded="listOpen"
            @click="listOpen = true"
          >
            <span class="relative grid size-8 shrink-0 place-items-center">
              <svg class="size-8 -rotate-90" viewBox="0 0 32 32" aria-hidden="true">
                <circle
                  cx="16"
                  cy="16"
                  r="13"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  class="text-dimmed"
                  opacity="0.3"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="13"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  class="text-primary transition-[stroke-dashoffset] duration-500 ease-out"
                  :stroke-dasharray="RING_CIRCUMFERENCE"
                  :stroke-dashoffset="ringOffset"
                />
              </svg>
              <span class="absolute grid place-items-center">
                <UIcon
                  v-if="headlineStep?.attention && headlineStep?.reachable"
                  name="i-lucide-alert-triangle"
                  class="size-3.5 text-warning"
                />
                <UIcon
                  v-else-if="headlineStep?.done"
                  name="i-lucide-check"
                  class="size-3.5 text-success"
                />
                <UIcon
                  v-else-if="headlineStep?.final"
                  name="i-lucide-flag"
                  class="size-3 text-highlighted"
                />
                <span
                  v-else
                  class="text-[0.6875rem] font-bold tabular-nums text-highlighted"
                >
                  {{ headlineStep?.number }}
                </span>
              </span>
            </span>

            <span class="min-w-0 flex-1">
              <span class="block truncate text-sm font-semibold leading-tight text-highlighted">
                {{ headlineStep?.label
                }}<span
                  v-if="headlineStep?.value"
                  class="font-normal text-muted"
                >
                  · {{ headlineStep.value }}</span>
              </span>
              <span class="mt-0.5 block truncate text-[0.6875rem] leading-tight text-muted">
                {{
                  $t('billing.page.checkout.progress.stepsDone', {
                    done: completed,
                    total,
                  })
                }}
              </span>
            </span>
          </button>

          <!-- Says what is left, and goes there. Two jobs, but they are the
               same intent: an outstanding count nobody can act on is just an
               accusation. -->
          <UButton
            size="xs"
            :color="attentionStep ? 'warning' : outstanding.length ? 'primary' : 'success'"
            :variant="outstanding.length || attentionStep ? 'soft' : 'ghost'"
            class="shrink-0"
            :icon="
              attentionStep
                ? 'i-lucide-alert-triangle'
                : outstanding.length
                  ? 'i-lucide-arrow-down'
                  : 'i-lucide-check-circle'
            "
            :label="
              outstanding.length
                ? $t(
                    'billing.page.checkout.progress.remainingShort',
                    { count: outstanding.length },
                    outstanding.length,
                  )
                : $t('billing.page.checkout.progress.ready')
            "
            :aria-label="
              outstanding.length
                ? $t('billing.page.checkout.progress.remaining', { count: outstanding.length }, outstanding.length)
                : $t('billing.page.checkout.progress.ready')
            "
            @click="jumpToOutstanding"
          />

          <UButton
            size="xs"
            color="neutral"
            variant="ghost"
            class="shrink-0"
            icon="i-lucide-list-checks"
            :aria-label="$t('billing.page.checkout.progress.showSteps')"
            @click="listOpen = true"
          />
        </div>

        <!-- ── Rail: laptop and up ─────────────────────────────────── -->
        <div class="hidden items-stretch gap-1 pl-1 pr-3 lg:flex">
          <button
            v-if="overflows"
            type="button"
            class="flex w-7 shrink-0 items-center justify-center rounded-md text-dimmed transition-colors hover:text-highlighted disabled:opacity-25 disabled:hover:text-dimmed"
            :disabled="atStart"
            :aria-label="$t('billing.page.checkout.progress.scrollBack')"
            @click="nudge(-1)"
          >
            <UIcon name="i-lucide-chevron-left" class="size-4" />
          </button>

          <ol
            ref="rail"
            class="checkout-rail flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto py-2"
            :style="{ maskImage: railMask, WebkitMaskImage: railMask }"
            @scroll.passive="syncEdges"
          >
            <li
              v-for="(step, index) in steps"
              :key="step.key"
              class="flex shrink-0 items-center"
            >
              <!-- The connector carries the progress: filled behind the
                   customer, hollow ahead of them. It is the whole reason the
                   rail reads as one path rather than nine loose chips. -->
              <span
                v-if="index > 0"
                aria-hidden="true"
                :class="[
                  'h-px w-4 shrink-0 rounded-full transition-colors',
                  steps[index - 1]?.done ? 'bg-primary/40' : 'bg-accented',
                ]"
              />

              <button
                type="button"
                :data-active="step.key === activeKey"
                :aria-current="step.key === activeKey ? 'step' : undefined"
                :aria-disabled="!step.reachable"
                :aria-label="`${step.label} — ${detailLabel(step)}`"
                :title="detailLabel(step)"
                :class="[
                  'group flex items-center gap-2 whitespace-nowrap rounded-full py-1 pl-1 pr-3 transition-all',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  step.key === activeKey ? 'bg-primary/10' : 'hover:bg-elevated',
                  step.reachable ? 'cursor-pointer' : 'cursor-default opacity-45',
                ]"
                @click="jumpTo(step)"
              >
                <span
                  :class="[
                    'grid size-6 shrink-0 place-items-center rounded-full text-[0.6875rem] font-bold transition-colors',
                    MARKER_TONE[toneOf(step)],
                  ]"
                >
                  <UIcon
                    v-if="step.attention && step.reachable"
                    name="i-lucide-alert-triangle"
                    class="size-3.5"
                  />
                  <UIcon v-else-if="step.done" name="i-lucide-check" class="size-3.5" />
                  <UIcon
                    v-else-if="step.final"
                    name="i-lucide-flag"
                    class="size-3.5"
                  />
                  <template v-else>{{ step.number }}</template>
                </span>
                <span :class="['text-xs font-medium transition-colors', chipTextClass(step)]">
                  {{ step.label }}
                </span>
              </button>
            </li>
          </ol>

          <button
            v-if="overflows"
            type="button"
            class="flex w-7 shrink-0 items-center justify-center rounded-md text-dimmed transition-colors hover:text-highlighted disabled:opacity-25 disabled:hover:text-dimmed"
            :disabled="atEnd"
            :aria-label="$t('billing.page.checkout.progress.scrollForward')"
            @click="nudge(1)"
          >
            <UIcon name="i-lucide-chevron-right" class="size-4" />
          </button>

          <!-- The standing answer to "can I order yet?", in its own gutter so
               it never scrolls away with the rail. -->
          <div class="flex shrink-0 items-center border-l border-default pl-3 ml-1">
            <UButton
              size="xs"
              :color="attentionStep ? 'warning' : outstanding.length ? 'primary' : 'success'"
              :variant="outstanding.length || attentionStep ? 'soft' : 'ghost'"
              :icon="
                attentionStep
                  ? 'i-lucide-alert-triangle'
                  : outstanding.length
                    ? 'i-lucide-arrow-down'
                    : 'i-lucide-check-circle'
              "
              :label="
                outstanding.length
                  ? $t(
                      'billing.page.checkout.progress.remaining',
                      { count: outstanding.length },
                      outstanding.length,
                    )
                  : $t('billing.page.checkout.progress.ready')
              "
              @click="jumpToOutstanding"
            />
          </div>
        </div>

        <!-- The measure of the whole thing, on the bar's own bottom edge.
             `role="progressbar"` so it is announced as progress rather than
             read as decoration. -->
        <div
          class="h-0.5 w-full bg-accented/60"
          role="progressbar"
          :aria-valuenow="percent"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="$t('billing.page.checkout.progress.title')"
        >
          <div
            class="h-full rounded-r-full bg-primary transition-[width] duration-500 ease-out"
            :style="{ width: `${percent}%` }"
          />
        </div>
      </div>
    </ClientOnly>

    <!-- Full list, on a phone. A bottom sheet rather than an expanding header:
         ten rows inside a sticky header would cover the page it is describing,
         and the thumb is at the bottom of the screen anyway. Same control the
         order summary already uses. -->
    <UDrawer
      v-model:open="listOpen"
      :title="$t('billing.page.checkout.progress.title')"
      :description="$t('billing.page.checkout.progress.stepsDone', {
        done: completed,
        total,
      })"
    >
      <template #body>
        <!-- Eleven steps is taller than a phone: the list scrolls inside the
             sheet rather than pushing its last rows off the screen, which is
             where the "review and confirm" destination sits. -->
        <ol
          class="max-h-[60vh] space-y-1 overflow-y-auto pb-[calc(0.5rem+env(safe-area-inset-bottom))]"
        >
          <li v-for="step in steps" :key="step.key">
            <button
              type="button"
              :aria-current="step.key === activeKey ? 'step' : undefined"
              :aria-disabled="!step.reachable"
              :class="[
                'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors',
                step.key === activeKey ? 'bg-primary/10' : 'hover:bg-elevated',
                step.reachable ? '' : 'opacity-50',
              ]"
              @click="jumpTo(step)"
            >
              <span
                :class="[
                  'grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold',
                  MARKER_TONE[toneOf(step)],
                ]"
              >
                <UIcon
                  v-if="step.attention && step.reachable"
                  name="i-lucide-alert-triangle"
                  class="size-4"
                />
                <UIcon v-else-if="step.done" name="i-lucide-check" class="size-4" />
                <UIcon v-else-if="step.final" name="i-lucide-flag" class="size-4" />
                <template v-else>{{ step.number }}</template>
              </span>

              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-medium text-highlighted">
                  {{ step.label }}
                </span>
                <span
                  :class="[
                    'block truncate text-xs',
                    step.attention && step.reachable ? 'text-warning' : 'text-muted',
                  ]"
                >
                  {{ detailLabel(step) }}
                </span>
              </span>

              <UIcon
                v-if="step.reachable"
                name="i-lucide-chevron-right"
                class="size-4 shrink-0 text-dimmed"
              />
            </button>
          </li>
        </ol>
      </template>
    </UDrawer>
  </div>
</template>

<style scoped>
/* Swiped or nudged with the gutter buttons — a scrollbar under the chips adds
   height on desktop and sits on top of the progress line. */
.checkout-rail {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.checkout-rail::-webkit-scrollbar {
  display: none;
}
</style>
